<template>
  <div class="fill-height">
    <template
      v-if="store.state.pages.Search.features.enable_filters && $route.params.searchId && loaded"
    >
      <Teleport
        v-if="search"
        to="#global-search"
      >
        <GlobalSearch
          @search-update="updateSearchParams($event)"
          :expanded="store.state.expandAppBarSmAndDown"
          :search="search"
        />
      </Teleport>
      <v-toolbar
        class="w-100pc bb-1"
        height="60"
        style="z-index: 1"
        absolute
        flat
      >
        <v-row class="c-black">
          <v-col
            class="d-flex align-center"
            cols="9"
            md="10"
          >
            <FilterBar
              v-model="search"
              @change="updateSearch()"
            />

            <template v-if="lists.length > 0">
              <FilterMenu
                :title="$vuetify.display.lgAndUp ? 'Favorites' : ''"
                tid="search_favorite_filter"
              >
                <template #icon-prefix>
                  <v-icon
                    class="fs-24 me-2 c-primary"
                    icon="favorite"
                  />
                </template>
                <template #icon-suffix>
                  <v-icon
                    class="fs-24 me-2 c-primary hidden"
                    aria-hidden
                  />
                </template>
                <template #card>
                  <div
                    v-for="list in lists"
                    :key="list.id"
                    class="pa-3 hover-bg-primary-lite"
                  >
                    <v-checkbox
                      v-model="listFilter"
                      @update:model-value="updateSearch()"
                      :label="list.name"
                      :value="list"
                      class="mt-0"
                      color="primary"
                      hide-details
                    />
                  </div>
                </template>
              </FilterMenu>
            </template>
          </v-col>

          <template v-if="store.state.pages.Search.download.enabled">
            <v-col
              class="d-flex justify-end align-center"
              cols="3"
              md="2"
            >
              <v-btn
                @click="$refs.download.open()"
                :ripple="false"
                class="d-flex d-lg-none focus-visible ma-0 pa-0"
                data-cy="download_favorites_link_mobile"
                data-tid="download_favorites_link"
                variant="text"
                tracked
              >
                {{ $bct('Download') }}
              </v-btn>
              <v-btn
                @click="$refs.download.open()"
                :ripple="false"
                class="d-none d-lg-block focus-visible"
                data-cy="download_favorites_link"
                data-tid="download_favorites_link"
                variant="text"
                tracked
              >
                {{ $bct('Download favorites') }}
              </v-btn>
            </v-col>
          </template>

          <DownloadFavoritesDialog
            ref="download"
            :lists="lists"
          />
        </v-row>
      </v-toolbar>
    </template>

    <SearchResults
      v-if="search"
      v-show="loaded"
      @change:page="updatePage($event)"
      @favorite:add="createFavorite($event[0], $event[1])"
      @favorite:list="createListAndFavorite($event[0], $event[1])"
      @favorite:remove="removeFavorite($event)"
      @redo="updateSearch"
      ref="results"
      :default-center="default_center"
      :favorites="favorites"
      :list-filters="listFilter"
      :lists="lists"
      :loaded="loaded"
      :matches="matches"
      :page="page"
      :pages="pages"
      :processing="processing"
      :search="search"
      :top="0"
    />
  </div>
</template>

<script setup>
import DownloadFavoritesDialog from '@/shared/components/search/DownloadFavoritesDialog.vue';
import FilterBar from '@/shared/components/search/FilterBar.vue';
import FilterMenu from '@/shared/components/form/FilterMenu.vue';
import GlobalSearch from '@/parent/components/searches/GlobalSearch.vue';
import SearchResults from '@/shared/components/search/SearchResults.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import { useRoute, useRouter } from 'vue-router';
import { SearchParams } from '@/shared/services/search_params';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const { api } = useApi();
const eventBus = useEventBus();
const $bct = inject('translator');
const { locale } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const allFiltersDialogIsVisible = ref(false);
const default_center = ref({ lat: 37.0902, lng: -95.7129 });
const favorites = ref([]);
const lists = ref([]);
const listFilter = ref(null);
const loaded = ref(false);
const matches = ref([]);
const moreFiltersDialogIsVisible = ref(false);
const page = ref(parseInt(route.query.page, 10) || 1);
const pages = ref(null);
const previousParams = ref(null);
const processing = ref(false);
const results = ref();
const search = ref(null);

function createFavorite(listId, providerId) {
  api.favorite.create({ list_id: listId, provider_id: providerId }, () => {
    loadFavorites();
  });
}

function createListAndFavorite(listName, providerId) {
  api.list.create({ name: listName }, (resp) => {
    createFavorite(resp.data.id, providerId);
  });
}

function removeFavorite(favoriteId) {
  api.favorite.destroy(favoriteId, () => {
    loadFavorites();
  });
}

async function load() {
  matches.value = [];
  const resp = await api.search.get(route.params.searchId);
  if (resp.status != 200) return router.replace({ path: '/' });

  await refreshSearchResults(resp.data);
}

function loadFavorites() {
  api.list.index((resp) => {
    lists.value = resp.data;
  });

  api.favorite.index((resp) => {
    favorites.value = resp.data;
  });
}

async function refreshSearchResults(data) {
  processing.value = true;
  matches.value = [];
  if (data) {
    let { distance } = data;
    if (distance) distance = parseInt(distance, 10);
    search.value = { ...data, distance };
  }

  const filters = { locale: locale.value, page: page.value };
  if (listFilter.value) {
    filters.list_id = listFilter.value.id;
  }

  const matchResp = await api.match.index(route.params.searchId, filters);
  const returningToFavoriteProvider = route.query.providerId;
  if (returningToFavoriteProvider) {
    results.value.changeMode('provider');
  }

  loaded.value = true;
  matches.value = matchResp.data;
  processing.value = false;
  pages.value = parseInt(matchResp.headers['x-page-count'] || 0, 10);

  if (returningToFavoriteProvider) {
    const selectedProviderIndex = matches.value.findIndex(
      (match) => match.id === route.query.providerId,
    );
    results.value.select(selectedProviderIndex);
  } else {
    results.value.changeMode('list');
    const resultsRefs = results.value.$refs || {};
    if (resultsRefs.match && resultsRefs.match[0]) {
      resultsRefs.match[0].$el.focus();
    } else {
      resultsRefs.noMatch.focus();
    }
  }
}

async function updatePage(newPage) {
  router.push({ query: { newPage } });
  page.value = newPage;
  await refreshSearchResults();
}

function updateSearch(bounds) {
  if (bounds) {
    search.value.sw_lat = bounds[0].lat();
    search.value.sw_lng = bounds[0].lng();
    search.value.ne_lat = bounds[1].lat();
    search.value.ne_lng = bounds[1].lng();
  } else {
    search.value.sw_lat = null;
    search.value.sw_lng = null;
    search.value.ne_lat = null;
    search.value.ne_lng = null;
  }

  moreFiltersDialogIsVisible.value = false;
  allFiltersDialogIsVisible.value = false;

  const updatedParams = new SearchParams(search.value, {
    ageGroups: search.value.ageGroups,
    listFilterId: listFilter.value?.id,
    dobSearch: store.state.pages.Search.enable_dob_search,
  }).asJson();

  if (previousParams.value !== JSON.stringify(updatedParams)) {
    processing.value = true;
    previousParams.value = JSON.stringify(updatedParams);
    page.value = 1;
    api.search.update(
      route.params.searchId,
      updatedParams,
      async (resp) => {
        await refreshSearchResults(resp.data);
      },
      (error) => {
        eventBus.chime(error.response.data.errors[0]);
        matches.value = [];
        processing.value = false;
      },
    );
  }
}

function updateSearchParams(newVal) {
  search.value = newVal;
  updateSearch();
}

onMounted(async () => {
  await load();
  search.value.exclude_closed = store.state.pages.Search.features.enable_closed_filter;
  eventBus.on('search-update', (value) => {
    search.value = value;
    updateSearch();
  });

  loadFavorites();
});
</script>
